// Credentialing wizard (#start) —— 步骤定义与选项常量
export const STEPS = [
    {
        key: 'identity',
        title: 'Basic information',
        sub: 'Let’s start with your basic details to set up your credentialing profile.',
    },
    {
        key: 'licenses',
        title: 'Licenses',
        sub: 'Enter your licenses, DEA, board certification, and upload supporting documents.',
    },
    { key: 'practice', title: 'Practice', sub: 'Add practice location and specialty details.' },
    {
        key: 'malpractice',
        title: 'Malpractice insurance certificate',
        sub: 'Upload your certificate of insurance (COI). You can request a copy from your malpractice carrier.',
    },
    {
        key: 'experience',
        title: 'Background',
        sub: 'Enter your healthcare work experience and educational background.',
    },
    {
        key: 'references',
        title: 'References',
        sub: 'Add professional references, such as a former colleague, supervisor, or someone you trained with, who can speak to your clinical background.',
    },
    { key: 'final', title: 'Verification', sub: 'A few last items to complete your application.' },
];

export const CITIZENSHIP = ['US citizen', 'Permanent resident (green card)', 'Other'];
export const CERT_BODY = ['ANCC', 'AANP', 'ABPN', 'ABMS', 'Other'];
export const COMPACTS = ['Nurse Licensure Compact (NLC)', 'PSYPACT', 'Counseling Compact'];
export const CARRIERS = ['HPSO', 'NSO', 'CM&F Group', 'Proliability', 'The Doctors Company', 'CNA', 'Other'];
export const PRACTICE_TYPES = [
    'Solo / independent practice',
    'Group practice',
    'Private practice',
    'Hospital-based',
    'Community health center',
    'Telehealth only',
    'Other',
];
export const CARE_DELIVERY = ['Telehealth', 'In-person', 'Hybrid'];
// 每条执业地址的「Services provided at this location」：作用域是单条地址（存进
// ProviderUniprofileAddressDto.careDelivery），取值沿用诊所级 CARE_DELIVERY 的那套，
// 只是文案按原型换成了 Virtual care only / In person only / Hybrid services
export const LOCATION_CARE_DELIVERY = [
    { label: 'Virtual care only', value: 'Telehealth' },
    { label: 'In person only', value: 'In-person' },
    { label: 'Hybrid services', value: 'Hybrid' },
];
export const locationCareDeliveryLabel = (v?: string) =>
    LOCATION_CARE_DELIVERY.find((o) => o.value === v)?.label || undefined;
export const RELATIONSHIPS = [
    'Supervisor',
    'Colleague / peer',
    'Collaborating physician',
    'Department chair / medical director',
    'Residency / program director',
    'Other',
];
export const ENTITY_TYPES = [
    'Individual / sole proprietor',
    'PLLC',
    'Professional corporation (PC)',
    'Single-member LLC',
    'S Corporation',
    'C Corporation',
    'Partnership',
    'Other',
];

// Verification 步骤的 6 个披露问题（回答 Yes 时需补充说明）
// id 直接使用接口 questionKey 枚举（LICENSE_ACTION / HOSP_PRIVILEGES / MALPRACTICE_CLAIM /
// CRIMINAL / SUBSTANCE / SANCTIONS），保证提交与回填按 questionKey 一一对应
export const DISCLOSURES: { id: string; q: string }[] = [
    {
        id: 'LICENSE_ACTION',
        q: 'Has your license to practice ever been denied, suspended, revoked, restricted, or voluntarily surrendered?',
    },
    {
        id: 'HOSP_PRIVILEGES',
        q: 'Have your hospital or clinical privileges ever been denied, suspended, revoked, restricted, or voluntarily relinquished?',
    },
    {
        id: 'MALPRACTICE_CLAIM',
        q: 'Have you ever been named in a malpractice lawsuit or had a malpractice claim filed against you?',
    },
    {
        id: 'CRIMINAL',
        q: 'Have you ever been convicted of a felony or misdemeanor (excluding minor traffic violations)?',
    },
    {
        id: 'SUBSTANCE',
        q: 'Do you currently have any physical or mental health condition, or substance use issue, that could affect your ability to practice?',
    },
    {
        id: 'SANCTIONS',
        q: 'Have you ever been excluded, sanctioned, or debarred from any federal or state healthcare program (e.g. Medicare, Medicaid)?',
    },
];

export const selectOptions = (arr: string[]) => arr.map((v) => ({ label: v, value: v }));

// Specialty offered：后端字典 HELLO_KLARITY_SPECIALTY 之外，credentialing 额外补充的选项。
// 字典项存 dict_key，这些补充项没有 dict_key，直接以文案本身作为值提交。
export const SPECIALTY_OTHERS = 'Others';

// D0（10/26，后端 #17）：Practice 步 "Your patients" 两题，存 code、显示文案
export const MONTHLY_VISITS_OPTIONS = [
    { value: 'NONE', label: 'None yet, just starting' },
    { value: 'UNDER_100', label: 'Fewer than 100 visits / month' },
    { value: '100_300', label: '100–300 visits / month' },
    { value: '300_750', label: '300–750 visits / month' },
    { value: 'OVER_750', label: '750+ visits / month' },
];

export const PATIENT_PAYER_MIX_OPTIONS = [
    { value: 'MOSTLY_SELF_PAY', label: 'Mostly self-pay' },
    { value: 'MIXED', label: 'A mix of self-pay and insurance' },
    { value: 'MOSTLY_INSURANCE', label: 'Mostly insurance' },
];
export const SPECIALTY_EXTRA_OPTIONS = selectOptions([
    'Urgent Care',
    'Physical Therapy',
    'Neurology',
    SPECIALTY_OTHERS,
]);

// Tax ID 既可能是 EIN（XX-XXXXXXX），也可能是个人 SSN（XXX-XX-XXXX，sole proprietor 常用），
// 分隔符位置不固定，因此只校验去掉非数字后是 9 位。文案在分步表单与 review 校验两处共用。
// 两种码用户都可能填，报错里不写具体格式，只提示位数
export const TAX_ID_ERROR = 'Must be 9 digits';
